import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Gift, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { api, errorMessage } from '@/lib/api/client';
import type {
  Customer,
  GiftCard,
  GiftCardHoldExpirySummary,
  PaginatedResponse,
} from '@/lib/api/types';
import { money, num, relativeTime, phone as phoneFmt, smartDate } from '@/lib/format';
import { useShortcutHint } from '@/app/StatusLine';
import { Badge, Code } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { CustomerPicker } from '@/ui/CustomerPicker';
import { DataTable, type Column } from '@/ui/DataTable';
import { ConfirmDialog, Dialog } from '@/ui/Dialog';
import { Field, Input, MoneyInput, SearchInput } from '@/ui/Field';
import { ChipGroup, FilterChip, Pagination, SubNav, SubNavItem } from '@/ui/Filters';
import { GiftCardDetail, giftCardTone } from '@/ui/GiftCardDetail';
import { EmptyState, Page, PageHeader, Toolbar } from '@/ui/Panel';
import { SplitView } from '@/ui/SplitView';
import { useDebounced } from '@/ui/useDebounced';
import { asText } from '@/lib/searchParams';

const PAGE_SIZE = 25;
const STATUSES = ['all', 'active', 'depleted', 'disabled'] as const;
type StatusFilter = (typeof STATUSES)[number];
const SORTS = ['created', 'code', 'balance', 'status'];

type GiftCardsSearch = {
  id?: number;
  q: string;
  status: StatusFilter;
  batchId?: number;
  customerId?: number;
  sort: string;
  order: 'asc' | 'desc';
  page: number;
};

export const Route = createFileRoute('/_app/gift-cards')({
  staticData: { crumb: 'Gift cards' },
  validateSearch: (s: Record<string, unknown>): GiftCardsSearch => ({
    id: Number(s.id) > 0 ? Number(s.id) : undefined,
    q: asText(s.q, ''),
    status: STATUSES.includes(s.status as StatusFilter)
      ? (s.status as StatusFilter)
      : 'all',
    batchId: Number(s.batchId) > 0 ? Number(s.batchId) : undefined,
    customerId: Number(s.customerId) > 0 ? Number(s.customerId) : undefined,
    sort: typeof s.sort === 'string' && SORTS.includes(s.sort) ? s.sort : 'created',
    order: s.order === 'asc' ? 'asc' : 'desc',
    page: Number(s.page) > 0 ? Math.floor(Number(s.page)) : 1,
  }),
  component: GiftCardsScreen,
});

function GiftCardsScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [term, setTerm] = useState(search.q);
  const debounced = useDebounced(term);
  const [issueOpen, setIssueOpen] = useState(false);

  useShortcutHint([
    { keys: 'j / k', label: 'Move' },
    { keys: '⏎', label: 'Inspect' },
  ]);

  useEffect(() => {
    if (debounced === search.q) return;
    void navigate({
      search: (prev) => ({ ...prev, q: debounced, page: 1 }),
      replace: true,
    });
  }, [debounced, search.q, navigate]);

  const list = useQuery({
    queryKey: [
      'gift-cards',
      'list',
      search.q,
      search.status,
      search.batchId,
      search.customerId,
      search.sort,
      search.order,
      search.page,
    ],
    queryFn: () =>
      api.get<PaginatedResponse<GiftCard>>('/gift-cards', {
        params: {
          search: search.q || undefined,
          status: search.status === 'all' ? undefined : search.status,
          batchId: search.batchId,
          customerId: search.customerId,
          sort: search.sort,
          order: search.order,
          page: search.page,
          pageSize: PAGE_SIZE,
        },
      }),
    placeholderData: (prev) => prev,
  });

  // Pending redemption holds about to lapse. This is a live money warning:
  // a hold that expires releases funds back mid-sale at a register.
  const holds = useQuery({
    queryKey: ['gift-cards', 'hold-expiry'],
    queryFn: () => api.get<GiftCardHoldExpirySummary>('/gift-cards/hold-expiry-summary'),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const rows = list.data?.data;
  const total = list.data?.total ?? 0;
  const summary = holds.data;

  const columns: Array<Column<GiftCard>> = [
    {
      id: 'code',
      header: 'Code',
      width: '8.5rem',
      mono: true,
      sortable: true,
      cell: (row) => <Code copyable>{row.code}</Code>,
    },
    {
      id: 'label',
      header: 'Name on card',
      cell: (row) => <span className="text-ink">{row.label || '—'}</span>,
    },
    {
      id: 'balance',
      header: 'Balance',
      width: '6.5rem',
      align: 'right',
      mono: true,
      sortable: true,
      cell: (row) => (
        <span className={row.balanceCents > 0 ? 'text-ink' : 'text-ink-faint'}>
          {money(row.balanceCents)}
        </span>
      ),
    },
    {
      id: 'initial',
      header: 'Issued for',
      width: '6.5rem',
      align: 'right',
      mono: true,
      hideBelow: 'md',
      cell: (row) => (
        <span className="text-ink-muted">{money(row.initialAmountCents)}</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      width: '6rem',
      sortable: true,
      cell: (row) => (
        <Badge tone={giftCardTone(row.status)} dot>
          {row.status}
        </Badge>
      ),
    },
    {
      id: 'recipient',
      header: 'Recipient',
      width: '7.5rem',
      mono: true,
      hideBelow: 'lg',
      cell: (row) => (
        <span className="text-ink-muted">
          {row.recipientPhone ? phoneFmt(row.recipientPhone) : '—'}
        </span>
      ),
    },
    {
      id: 'batch',
      header: 'Batch',
      width: '4.5rem',
      mono: true,
      align: 'right',
      hideBelow: 'lg',
      cell: (row) => <span className="text-ink-faint">{row.batchId ?? '—'}</span>,
    },
    {
      id: 'created',
      header: 'Issued',
      width: '5.5rem',
      mono: true,
      sortable: true,
      hideBelow: 'lg',
      cell: (row) => <span className="text-ink-faint">{smartDate(row.createdAt)}</span>,
    },
  ];

  return (
    <Page>
      <PageHeader
        eyebrow="People"
        title="Gift cards"
        subtitle="Issued cards, balances and movement history"
        actions={
          <Button
            variant="primary"
            size="sm"
            icon={<Plus className="size-3.5" />}
            onClick={() => setIssueOpen(true)}
          >
            Issue card
          </Button>
        }
        tabs={
          <SubNav>
            <SubNavItem active onClick={() => undefined}>
              Cards
            </SubNavItem>
            <SubNavItem
              active={false}
              onClick={() =>
                void navigate({ to: '/gift-cards/batches', search: { page: 1 } })
              }
            >
              Batches
            </SubNavItem>
          </SubNav>
        }
      />

      {summary && summary.expiringCount > 0 ? (
        <div className="border-warn/30 bg-warn-muted text-ink flex shrink-0 items-center gap-2 border-b px-4 py-2 text-xs">
          <AlertTriangle className="text-warn size-3.5 shrink-0" />
          <span>
            <strong className="font-mono tabular-nums">
              {num(summary.expiringCount)}
            </strong>{' '}
            redemption {summary.expiringCount === 1 ? 'hold' : 'holds'} worth{' '}
            <strong className="font-mono tabular-nums">
              {money(summary.expiringAuthorizedCents)}
            </strong>{' '}
            expire within {summary.warningWindowMinutes} minutes. When a hold lapses the
            funds go back on the card mid-sale.
          </span>
          {summary.holds[0] ? (
            <span className="text-2xs text-ink-muted ml-auto shrink-0 font-mono">
              next {relativeTime(summary.holds[0].expiresAt)} · card{' '}
              {summary.holds[0].giftCardCode}
            </span>
          ) : null}
        </div>
      ) : null}

      <Toolbar>
        <SearchInput
          size="sm"
          className="w-60"
          value={term}
          onValueChange={setTerm}
          placeholder="Card code…"
          kbd="/"
        />
        <ChipGroup<StatusFilter>
          value={search.status}
          onChange={(status) =>
            void navigate({ search: (prev) => ({ ...prev, status, page: 1 }) })
          }
          options={[
            { value: 'all', label: 'All' },
            { value: 'active', label: 'Active' },
            { value: 'depleted', label: 'Depleted' },
            { value: 'disabled', label: 'Disabled' },
          ]}
        />
        {search.batchId ? (
          <FilterChip
            label="Batch"
            value={search.batchId}
            onRemove={() =>
              void navigate({
                search: (prev) => ({ ...prev, batchId: undefined, page: 1 }),
              })
            }
          />
        ) : null}
        {search.customerId ? (
          <FilterChip
            label="Customer"
            value={search.customerId}
            onRemove={() =>
              void navigate({
                search: (prev) => ({ ...prev, customerId: undefined, page: 1 }),
              })
            }
          />
        ) : null}
        <div className="ml-auto">
          <Pagination
            page={search.page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={(page) =>
              void navigate({ search: (prev) => ({ ...prev, page }) })
            }
          />
        </div>
      </Toolbar>

      <div className="min-h-0 flex-1">
        <SplitView
          storageKey="gift-cards"
          detail={search.id ? <GiftCardDetail id={search.id} compact /> : undefined}
          onCloseDetail={() =>
            void navigate({ search: (prev) => ({ ...prev, id: undefined }) })
          }
          placeholder={
            <EmptyState
              icon={<Gift />}
              title="No card selected"
              description="Pick a card to see its balance, recipient and full movement history."
            />
          }
          list={
            <DataTable
              rows={rows}
              columns={columns}
              rowKey={(row) => row.id}
              loading={list.isLoading}
              error={list.error}
              onRetry={() => void list.refetch()}
              isRowActive={(row) => row.id === search.id}
              rowTone={(row) => (row.status === 'disabled' ? 'negative' : null)}
              onRowOpen={(row) =>
                void navigate({ search: (prev) => ({ ...prev, id: row.id }) })
              }
              sort={{
                by: search.sort,
                dir: search.order,
                onChange: (by, dir) =>
                  void navigate({
                    search: (prev) => ({ ...prev, sort: by, order: dir, page: 1 }),
                  }),
              }}
              empty={{
                icon: <Gift />,
                title: search.q ? 'No card matches' : 'No gift cards',
                description: search.q
                  ? `Nothing found for “${search.q}”.`
                  : 'Issue a single card here, or create a batch for a bulk order.',
              }}
              footer={<span>{num(total)} cards</span>}
            />
          }
        />
      </div>

      <IssueCardDialog open={issueOpen} onOpenChange={setIssueOpen} />
    </Page>
  );
}

/* ── Issue a single card ──────────────────────────────────────────────────────
   Money path: the card is spendable the moment the server answers, so this
   confirms first and never guesses the outcome.                              */

function IssueCardDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const navigate = Route.useNavigate();
  const [amount, setAmount] = useState<number | null>(null);
  const [label, setLabel] = useState('');
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [confirming, setConfirming] = useState(false);

  const reset = () => {
    setAmount(null);
    setLabel('');
    setCustomer(null);
    setConfirming(false);
  };

  const issue = useMutation({
    mutationFn: () =>
      api.post<GiftCard>('/gift-cards', {
        amountCents: amount ?? 0,
        ...(label.trim() ? { label: label.trim() } : {}),
        ...(customer ? { customerId: Number(customer.id) } : {}),
      }),
    onSuccess: (card) => {
      void qc.invalidateQueries({ queryKey: ['gift-cards'] });
      onOpenChange(false);
      reset();
      toast.success(`Card ${card.code} issued for ${money(card.balanceCents)}.`);
      void navigate({ search: (prev) => ({ ...prev, id: card.id }) });
    },
    onError: (err) => {
      setConfirming(false);
      toast.error(errorMessage(err));
    },
  });

  const valid = amount !== null && amount > 0;

  return (
    <>
      <Dialog
        open={open && !confirming}
        onOpenChange={(next) => {
          if (!next) reset();
          onOpenChange(next);
        }}
        title="Issue a gift card"
        description="Creates one active card with a live balance."
        width="sm"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!valid}
              onClick={() => setConfirming(true)}
            >
              Review
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Field label="Amount" required hint="The starting balance on the card.">
            <MoneyInput autoFocus value={amount} onValueChange={setAmount} />
          </Field>
          <Field label="Name on card" hint="Printed on the card image. Optional.">
            <Input
              value={label}
              onChange={(e) => setLabel(e.currentTarget.value)}
              maxLength={255}
            />
          </Field>
          <Field label="Owner" hint="Optional — links the card to a customer record.">
            <CustomerPicker value={customer} onChange={setCustomer} />
          </Field>
        </div>
      </Dialog>

      <ConfirmDialog
        open={open && confirming}
        onOpenChange={(next) => {
          if (!next) setConfirming(false);
        }}
        title="Issue this gift card?"
        description="The card is spendable at every register as soon as it exists."
        confirmLabel="Issue card"
        variant="primary"
        loading={issue.isPending}
        consequences={[
          `Creates a card carrying ${money(amount ?? 0)} of real balance`,
          'No charge is recorded here — take payment separately',
          customer ? `Linked to ${customer.name}` : 'Not linked to a customer',
        ]}
        onConfirm={() => issue.mutate()}
      />
    </>
  );
}
